// Core domain types. Coordinates use GeoJSON order [lon, lat] for turf interop unless noted.

/** A position in GeoJSON order: [longitude, latitude], WGS84 decimal degrees. */
export type LonLat = [number, number];

/** Fix quality as reported in NMEA GGA field 6. */
export enum FixQuality {
  Invalid = 0,
  /** Standalone GPS (~meters). */
  GPS = 1,
  /** Differential GPS. */
  DGPS = 2,
  PPS = 3,
  /** RTK fixed integer ambiguities — centimeter accuracy. The target state for guidance. */
  RTKFixed = 4,
  /** RTK float — ambiguities unresolved, ~decimeter. Usable but degraded. */
  RTKFloat = 5,
  /** Dead reckoning / estimated. */
  Estimated = 6,
  Manual = 7,
  /** Simulator-produced fix. */
  Simulation = 8,
}

/** A single GNSS position fix, normalized from NMEA (GGA + RMC). */
export interface GnssFix {
  /** Unix epoch milliseconds (UTC). */
  timestamp: number;
  /** Latitude, WGS84 decimal degrees. */
  lat: number;
  /** Longitude, WGS84 decimal degrees. */
  lon: number;
  quality: FixQuality;
  /** Satellites used in the solution. */
  satellites: number;
  /** Horizontal dilution of precision. */
  hdop: number;
  /** Horizontal accuracy estimate in meters, when the source reports one (e.g. phone GPS). */
  accuracyM?: number;
  /** Altitude above the geoid, meters. */
  altitude?: number;
  /** Speed over ground, meters/second. */
  speed?: number;
  /** Course over ground (true heading), degrees [0, 360). */
  heading?: number;
}

/** True when a fix is trustworthy enough to drive guidance from. Plain GPS (~meters) is included so
 *  a phone can act as a test-rig source — guidance will show metre-level deviation, not cm, which is
 *  expected and surfaced via the accuracy readout. */
export function isUsableFix(fix: Pick<GnssFix, 'quality'>): boolean {
  return (
    fix.quality === FixQuality.RTKFixed ||
    fix.quality === FixQuality.RTKFloat ||
    fix.quality === FixQuality.DGPS ||
    fix.quality === FixQuality.GPS ||
    fix.quality === FixQuality.Simulation
  );
}

/** A guidance lane (swath centerline) the vehicle should follow. */
export interface Lane {
  /** Signed lane index; 0 is the AB baseline, +k to the right of A→B, -k to the left. */
  index: number;
  /** Centerline polyline, [lon, lat]. Concave fields may split one index into multiple Lane records. */
  coordinates: LonLat[];
}

/** A generated set of parallel driving lanes for a field. */
export interface Course {
  lanes: Lane[];
  /** Implement / swath working width, meters. */
  widthM: number;
  /** Bearing of the A→B baseline, degrees true [0, 360). */
  headingDeg: number;
  /** Projected CRS used for the metric math (EPSG:32632 or 32633). */
  epsg: number;
  /** The AB baseline the lanes were generated from. */
  ab: { a: LonLat; b: LonLat };
}

/** Which way the driver should steer to return to the lane center. */
export type SteerDirection = 'left' | 'right' | 'on';

/** Live guidance derived from a fix + a course. */
export interface Guidance {
  /** Index of the lane currently being followed (nearest). */
  activeLaneIndex: number;
  /**
   * Signed cross-track error in meters.
   * Negative = vehicle is LEFT of the lane center; positive = RIGHT of it.
   * The magnitude is the deviation to display; steer toward zero.
   */
  crossTrackM: number;
  /** Direction to steer to correct the cross-track error. */
  steer: SteerDirection;
  /** Heading error (vehicle heading − lane bearing), normalized to [-180, 180], if heading known. */
  headingErrorDeg?: number;
  /** Nearest point on the active lane centerline, [lon, lat]. */
  nearestPoint: LonLat;
}
